<template>
  <div class="min-h-screen bg-gradient-to-b from-white to-gray-50">
    <!-- Hero Section -->
    <section class="py-16 md:py-24">
      <div class="container mx-auto px-4">
        <div class="text-center max-w-4xl mx-auto">
          <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            {{ $t('pricing.title') }}
          </h1>
          <p class="text-xl md:text-2xl text-gray-600 mb-8">
            {{ $t('pricing.subtitle') }}
          </p>
        </div>
      </div>
    </section>

    <!-- Pricing Card Section -->
    <section class="py-12 md:py-16">
      <div class="container mx-auto px-4">
        <div class="max-w-5xl mx-auto">
          <div class="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-blue-600">
            <div class="grid md:grid-cols-2 gap-0">
              <!-- Left Column - Header Info -->
              <div class="p-8 md:p-12 bg-gradient-to-br from-blue-50 to-white">
                <div class="mb-8">
                  <h2 class="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
                    {{ $t('pricing.tier.name') }}
                  </h2>
                  <button
                    @click="$router.push('/demo')"
                    class="px-6 py-3 border-2 border-blue-600 text-blue-600 rounded-lg font-semibold hover:bg-blue-600 hover:text-white transition-colors"
                  >
                    {{ $t('pricing.cta.requestDemo') }}
                  </button>
                </div>
              </div>

              <!-- Right Column - Price -->
              <div class="p-8 md:p-12 bg-blue-600 text-white flex flex-col justify-center items-center text-center">
                <p class="text-lg mb-2">Starting at</p>
                <div class="mb-4">
                  <span class="text-5xl md:text-6xl font-bold">{{ pricingTier.price }}</span>
                  <span class="text-2xl">/month</span>
                  <sup class="text-sm">†</sup>
                </div>
                <p class="text-sm opacity-90">
                  <sup>†</sup>{{ $t('pricing.tier.priceNote') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Benefits Section -->
    <section class="py-12 md:py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="max-w-5xl mx-auto">
          <div class="grid md:grid-cols-2 gap-12 items-center">
            <!-- Left - Illustration/Icon -->
            <div class="order-2 md:order-1">
              <div class="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 md:p-12">
                <div class="space-y-6">
                  <div class="flex items-center space-x-3">
                    <svg class="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                    </svg>
                    <span class="text-lg font-semibold text-blue-900">{{ $t('pricing.benefits.noUserFee') }}</span>
                  </div>
                  <div class="flex items-center space-x-3">
                    <svg class="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                    </svg>
                    <span class="text-lg font-semibold text-blue-900">{{ $t('pricing.benefits.noRevenueShare') }}</span>
                  </div>
                  <div class="flex items-center space-x-3">
                    <svg class="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                    </svg>
                    <span class="text-lg font-semibold text-blue-900">{{ $t('pricing.benefits.consolidatedData') }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right - Text -->
            <div class="order-1 md:order-2">
              <h2 class="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
                {{ $t('pricing.benefits.title') }}
              </h2>
              <p class="text-lg text-gray-600 leading-relaxed">
                {{ $t('pricing.benefits.subtitle') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="py-12 md:py-16 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="max-w-5xl mx-auto">
          <h2 class="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
            What's Included
          </h2>
          <p class="text-lg text-center text-gray-600 mb-12">
            Everything you need to manage your omnichannel business
          </p>

          <!-- Features Grid -->
          <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div
              v-for="feature in pricingTier.features"
              :key="feature.key"
              class="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow"
            >
              <div class="flex items-start space-x-3">
                <svg class="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                </svg>
                <span class="text-gray-800 font-medium">{{ $t(feature.label) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-16 md:py-24 bg-gradient-to-br from-blue-600 to-blue-700">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto text-center">
          <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Simplify Your Online Retail Management?
          </h2>
          <p class="text-xl text-blue-100 mb-8">
            Join brands that trust E2M to streamline their multi-channel operations.
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              @click="$router.push('/demo')"
              class="px-8 py-4 bg-white text-blue-600 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors shadow-lg"
            >
              {{ $t('pricing.cta.requestDemo') }}
            </button>
            <button
              @click="$router.push('/contact')"
              class="px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600 transition-colors"
            >
              {{ $t('pricing.cta.contactSales') }}
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { pricingTier } from '@/data/pricing'
</script>
